import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionCheckBoxField, QuestionGiinField } from 'components/Questionnaires/fields';
import { requiredValidator, lengthValidator, validateQuestion } from 'components/Questionnaires/validators';

export const QuestionGiinBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? { appliedFor: '' });

  const giinValidators = {
    conditions: [
      { condition: requiredValidator, message: 'questionnaire.giin.errorMessage' },
      { condition: lengthValidator, message: 'questionnaire.giin.lengthErrorMessage' }
    ]
  };

  const [value, setValue] = useState({
    id: 'value',
    label: 'questionnaire.giin.label',
    component: QuestionGiinField,
    componentProps: {
      length: 16
    },
    info: {
      instructions: block.componentProps?.giinInstructions ?? 'questionnaire.giin.info'
    },
    visible: true,
    validation: giinValidators
  });

  const appliedFor = {
    id: 'appliedFor',
    component: QuestionCheckBoxField,
    componentProps:
    {
      option: 'questionnaire.giin.appliedFor.label'
    },
    visible: block.componentProps?.showAppliedFor !== undefined ? block.componentProps?.showAppliedFor : true,
    answer: answer?.appliedFor ?? undefined
  };

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isValid = () => {
    if (block.componentProps?.showAppliedFor === false) {
      return value.isValid;
    }
    if (answer.value === undefined && (appliedFor.answer === '' || appliedFor.answer === undefined)) {
      return undefined;
    }
    return value.isValid || (!answer.value && answer.appliedFor === 'true');
  };

  const onQuestionChange = (q, v) => {
    switch (q.id) {
      case 'value': {
        const newValue = { ...q, answer: v };
        validateQuestion(newValue);
        setValue(newValue);
        setAnswer({ ...answer, value: v });
        break;
      }
      case 'appliedFor': {
        const validators = v === 'true' ? null : giinValidators;
        const questionProps = v === 'true' ? { isValid: true, errorMessages: undefined, disabled: true } : { disabled: false };
        const newValue = { ...value, ...questionProps, ...{ validation: validators } };
        validateQuestion(newValue);
        setValue(newValue);
        setAnswer({ ...answer, appliedFor: v });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      if (Object.keys(answer).length > 0 && answer.constructor === Object) {
        dispatch(answerBlock(block.id, answer, isValid()));
      } else {
        onQuestionChange(value, undefined);
        onQuestionChange(appliedFor, undefined);
      }
    } else {
      onAnswer(block, answer, isValid());
    }
    dispatch(answerBlock(block.id, answer, isValid()));
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      onQuestionChange({ ...value, isForceEdited: true }, answer?.value ?? '');
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ value.id } blockId={ blockIdentifier } question={ value } { ...value.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ appliedFor.id } blockId={ blockIdentifier } question={ appliedFor } { ...appliedFor.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
  </React.Fragment>);
};

QuestionGiinBlock.displayName = 'QuestionGiinBlock';

QuestionGiinBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
};

QuestionGiinBlock.defaultProps = {
  onAnswer: null
};

export default QuestionGiinBlock;
